/**
 * LinkedIn Outreach Types
 *
 * Data structures for the outbound outreach CRM system.
 * Collection: linkedin_outreach/{docId}
 * Subcollection: linkedin_outreach/{docId}/activities/{activityId}
 * Collection: linkedin_metrics/{dateKey}
 */

import type { StatusChange } from './leads';

// ============================================
// OUTREACH STATUS PIPELINE
// ============================================

export type OutreachStatus =
  | 'identified'
  | 'connection_sent'
  | 'connected'
  | 'messaged'
  | 'responded'
  | 'warm'
  | 'discovery_booked'
  | 'demo_booked'
  | 'converted'
  | 'not_interested'
  | 'no_response'
  | 'deferred';

export const OUTREACH_STATUS_LABELS: Record<OutreachStatus, string> = {
  identified: 'Identified',
  connection_sent: 'Connection Sent',
  connected: 'Connected',
  messaged: 'Messaged',
  responded: 'Responded',
  warm: 'Warm',
  discovery_booked: 'Discovery Booked',
  demo_booked: 'Demo Booked',
  converted: 'Converted',
  not_interested: 'Not Interested',
  no_response: 'No Response',
  deferred: 'Deferred',
};

export const OUTREACH_STATUS_COLORS: Record<OutreachStatus, string> = {
  identified: '#6b7280',
  connection_sent: '#3b82f6',
  connected: '#8b5cf6',
  messaged: '#06b6d4',
  responded: '#f59e0b',
  warm: '#f97316',
  discovery_booked: '#ec4899',
  demo_booked: '#a855f7',
  converted: '#22c55e',
  not_interested: '#ef4444',
  no_response: '#94a3b8',
  deferred: '#78716c',
};

export const ALL_OUTREACH_STATUSES: OutreachStatus[] = [
  'identified',
  'connection_sent',
  'connected',
  'messaged',
  'responded',
  'warm',
  'discovery_booked',
  'demo_booked',
  'converted',
  'not_interested',
  'no_response',
  'deferred',
];

// Active pipeline statuses (for funnel chart)
export const PIPELINE_STATUSES: OutreachStatus[] = [
  'identified',
  'connection_sent',
  'connected',
  'messaged',
  'responded',
  'warm',
  'discovery_booked',
  'demo_booked',
  'converted',
];

// ============================================
// CONTACT METHOD & ACTIVITY TYPES
// ============================================

export type ContactMethod =
  | 'connection_request'
  | 'dm'
  | 'comment'
  | 'inmail'
  | 'email'
  | 'phone'
  | 'referral_intro';

export type ActivityDirection = 'outbound' | 'inbound';
export type ActivityOutcome = 'positive' | 'neutral' | 'negative' | 'no_response';
export type ICPFit = 'strong' | 'moderate' | 'weak';
export type Priority = 'high' | 'medium' | 'low';

export const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  connection_request: 'Connection Request',
  dm: 'Direct Message',
  comment: 'Comment',
  inmail: 'InMail',
  email: 'Email',
  phone: 'Phone Call',
  referral_intro: 'Referral Intro',
};

export const CONTACT_METHOD_ICONS: Record<ContactMethod, string> = {
  connection_request: 'UserPlus',
  dm: 'MessageSquare',
  comment: 'MessageCircle',
  inmail: 'Mail',
  email: 'AtSign',
  phone: 'Phone',
  referral_intro: 'Users',
};

export const ACTIVITY_OUTCOME_LABELS: Record<ActivityOutcome, string> = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
  no_response: 'No Response',
};

export const ACTIVITY_OUTCOME_COLORS: Record<ActivityOutcome, string> = {
  positive: '#22c55e',
  neutral: '#f59e0b',
  negative: '#ef4444',
  no_response: '#94a3b8',
};

export const ICP_FIT_LABELS: Record<ICPFit, string> = {
  strong: 'Strong Fit',
  moderate: 'Moderate Fit',
  weak: 'Weak Fit',
};

export const ICP_FIT_COLORS: Record<ICPFit, string> = {
  strong: '#22c55e',
  moderate: '#f59e0b',
  weak: '#ef4444',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const PRIORITY_COLORS: Record<Priority, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#6b7280',
};

// ============================================
// CORE INTERFACES
// ============================================

export interface OutreachContact {
  id: string;

  // Identity
  firstName: string;
  lastName: string;
  linkedinUrl?: string;
  email?: string;
  phone?: string;
  company: string;
  role?: string;
  location?: string;
  website?: string;
  birthday?: string;              // "12 Mar" or "12 Mar 1985" — year optional

  // Qualification
  estimatedBrokerCount?: string;
  numberOfOffices?: string;
  aggregator?: string;
  icpFit?: ICPFit;

  // Pipeline state
  status: OutreachStatus;
  statusHistory: StatusChange[];
  lastContactedAt?: Date | { toDate: () => Date };
  nextFollowUpDate?: Date | { toDate: () => Date };
  activityCount: number;

  // Linkage to inbound leads
  linkedLeadRef?: string;
  linkedLeadType?: string;
  linkedLeadId?: string;

  // Admin
  notes?: string;
  tags?: string[];
  priority?: Priority;
  foundVia?: string;

  createdAt: Date | { toDate: () => Date };
  updatedAt?: Date | { toDate: () => Date };
}

export interface OutreachActivity {
  id: string;
  contactMethod: ContactMethod;
  direction: ActivityDirection;
  summary: string;
  content?: string;
  response?: string;
  outcome?: ActivityOutcome;
  linkedinPostUrl?: string;
  timestamp: Date | { toDate: () => Date };
  createdBy: string;
}

export interface LinkedInDailyMetrics {
  id?: string;
  date: string;
  // Post metrics
  impressions?: number;
  reactions?: number;
  comments?: number;
  reposts?: number;
  // Profile metrics
  profileViews?: number;
  searchAppearances?: number;
  // Connection metrics
  connectionsSent?: number;
  connectionsAccepted?: number;
  connectionsPending?: number;
  // Outreach metrics
  dmsSent?: number;
  dmsReceived?: number;
  commentsLeft?: number;
  // Goals
  newContactsAdded?: number;
  discoveryCalls?: number;

  timestamp: Date | { toDate: () => Date };
  updatedAt?: Date | { toDate: () => Date };
}

// ============================================
// DASHBOARD STATS
// ============================================

export interface OutreachDashboardStats {
  totalContacts: number;
  contactsThisWeek: number;
  responseRate: number;
  conversionRate: number;
  byStatus: Record<OutreachStatus, number>;
  recentActivities: (OutreachActivity & { contactName: string; contactId: string })[];
  followUpsDueToday: OutreachContact[];
}

// ============================================
// HELPERS
// ============================================

export function getContactDisplayName(contact: OutreachContact): string {
  return `${contact.firstName} ${contact.lastName}`.trim() || contact.company;
}

export function normalizeLinkedInUrl(url: string): string {
  if (!url) return '';
  let normalized = url.toLowerCase().trim();
  // Remove query params
  const queryIndex = normalized.indexOf('?');
  if (queryIndex !== -1) normalized = normalized.substring(0, queryIndex);
  // Remove trailing slashes
  normalized = normalized.replace(/\/+$/, '');
  // Ensure it starts with https://
  if (!normalized.startsWith('http')) {
    normalized = `https://www.linkedin.com/in/${normalized}`;
  }
  return normalized;
}
